import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from "react";

import { sentenceCase } from "change-case";
import { type ChartOptions } from "chart.js";
import { addDays, differenceInDays } from "date-fns";
import { motion } from "framer-motion";
import {
  cloneDeep,
  compact,
  flatten,
  isEmpty,
  isEqual,
  orderBy,
  uniq,
  uniqBy,
} from "lodash";
import moment from "moment";
import { Line } from "react-chartjs-2";
import { type ChartJSOrUndefined } from "react-chartjs-2/dist/types";

import salesChartLoading from "@assets/sales-v3-chart-loading.gif";
import { TitleSlide } from "@components/title-slide-view";
import { useUser } from "@components/user-context";
import { useQueryMetadata } from "@components/v2/modals";
import {
  useBuildAnnotationDataset,
  type AnnotationDataset,
} from "@features/custom-annotations";
import { useUniversalBenchmarksLoader } from "@features/universal-benchmarks";
import { useDesignElementManager } from "@hooks/use-design-element-manager";
import { type User } from "@internal/user";
import {
  CreativesFilterClauseTypeEnum,
  type GetSalesReportV3Augmented_me_salesTableReportV3Augmented,
  makeBenchmarkRows,
  makeFirstTimeVsReturningRows,
  makeOverviewRows,
  makeTrafficRows,
  type AbstractUser,
  type GetSalesReportV3AugmentedVariables,
  type SalesPageMetric,
  type GetSalesReportV3Augmented_me_salesTableReportV3Augmented_actual,
} from "@north-beam/nb-client-side";
import {
  type constants,
  type DateInterval,
  type ITimeGranularityV2,
} from "@north-beam/nb-common";

import { usePageConfiguration } from "../hooks";
import { useActiveTab } from "../hooks/use-active-tab";
import { useAvailableMetrics } from "../hooks/use-available-metrics";
import {
  type ChartConfig,
  type Compare,
  type MainMetric,
} from "../hooks/use-chart-state";
import {
  resolveBaseMetrics,
  useSalesReportV3,
} from "../hooks/use-sales-report";
import { useShowV1Forecasts } from "../hooks/use-show-v1-forecasts";
import { useTabState } from "../hooks/use-tab-state";
import {
  useDefaultTableSelectionIsSelectAll,
  useSplitView,
} from "../hooks/use-table-selection";
import {
  enrichInclusionsAndExclusions,
  reconcileAppliedAndProposedTabFilters,
} from "../tabs/components/tabs";
import {
  debouncedResize,
  generateDateLabels,
  getChartOptions,
  getDateOnly,
} from "../utils/chart";
import {
  preprocessForChart,
  type ChartDatasets,
} from "../utils/chart-preprocessing";
import { deriveComparisonDateRange } from "../utils/compare-dates-helpers";
import { buildCompareQueryMetadata } from "../utils/compare-query";

import { ChartConfigurator } from "./chart-configurator";
import { ChartLegend } from "./chart-legend";
import { downloadCsv } from "./csv";
import { usePlugins } from "./plugins";
import { updateScaleMax } from "./plugins/align-axes-scale-plugin";
import { setChartDataManagerOptions } from "./plugins/chart-data-manager-plugin";
import { setYAxesTitles } from "./plugins/y-scale-titles-plugin";

interface ChartProps {
  metrics: SalesPageMetric[];
  chartConfig: ChartConfig;
  chartIndex: number;
  processedData: ChartDatasets;
  annotationDataset: AnnotationDataset;
  processedDataCompare1: ChartDatasets | null;
  processedDataCompare2: ChartDatasets | null;
  salesReport?: GetSalesReportV3Augmented_me_salesTableReportV3Augmented_actual;
  benchmarkMainData: number | null;
  benchmarkCompare1Data: number | null;
  benchmarkCompare2Data: number | null;
}

export interface ChartImperativeRef {
  exportData(): void;
}

export type Dimensions = {
  [dimension: string]: string;
};
const datasets: any[] = [];

function createComparisonVariables(
  comparison: Compare,
  mainMetric: MainMetric,
  commonVariables: Omit<GetSalesReportV3AugmentedVariables, "granularity">,
): {
  variables: Partial<GetSalesReportV3AugmentedVariables>;
  useMainQuery: boolean;
} {
  let breakdownFilters:
    | {
        breakdown: string;
        value: string;
      }[]
    | null = null;

  if (comparison.filters.length) {
    breakdownFilters = flatten(
      comparison.filters.map((breakdown) =>
        breakdown.values.map((value) => ({
          breakdown: breakdown.breakdown.key,
          value: value.value,
        })),
      ),
    );
  }

  if (commonVariables.breakdownFilters) {
    if (!breakdownFilters) {
      breakdownFilters = [];
    }

    commonVariables.breakdownFilters.forEach((e) => {
      if (e) {
        breakdownFilters?.push(e);
      }
    });

    breakdownFilters = uniqBy(
      breakdownFilters,
      (e) => `${e.breakdown} ${e.value}`,
    );
  }

  const showV1Forecasts =
    commonVariables.showV1Forecasts &&
    comparison.queryValue?.toLowerCase().includes("forecast");

  const hashMainMetrics = mainMetric.filters
    .map((breakdown) =>
      breakdown.values.map(
        (value) => `breakdown:${breakdown.breakdown.key}-value:${value.value}`,
      ),
    )
    .sort();
  const hashComparisonMetrics = comparison.filters
    .map((breakdown) =>
      breakdown.values.map(
        (value) => `breakdown:${breakdown.breakdown.key}-value:${value.value}`,
      ),
    )
    .sort();

  const metricFiltersCopy = cloneDeep(commonVariables.metricFilters);
  if (comparison.queryKey === "attributionModel") {
    metricFiltersCopy?.metricClauses?.forEach((metricClause) => {
      if (metricClause.metric.details) {
        metricClause.metric.details.am = comparison.queryValue;
      }
    });
  }

  const useNewQuery =
    !isEqual(hashMainMetrics, hashComparisonMetrics) ||
    comparison.queryKey !== "metricName" ||
    mainMetric.ignoreTableFilters !== comparison.ignoreTableFilters ||
    !isEqual(metricFiltersCopy, commonVariables.metricFilters) ||
    showV1Forecasts;

  return {
    variables: {
      [comparison.queryKey === "attributionModel"
        ? "attributionMethod"
        : comparison.queryKey]: comparison.queryValue,
      ...(breakdownFilters && { breakdownFilters }),
      metricFilters: metricFiltersCopy,
      ...(comparison.ignoreTableFilters && { metricFilters: null }),
      showV1Forecasts,
    },
    useMainQuery: !useNewQuery,
  };
}

function createCompareDateRangeComparisonVariables(
  comparison: Compare,
  dateInterval: DateInterval,
  compareDateRange: constants.CompareDateRange | null,
  timezone: User["timezone"],
  timeGranularity: ITimeGranularityV2,
  commonVariables: Omit<GetSalesReportV3AugmentedVariables, "granularity">,
) {
  const comparisonDateRange = compareDateRange
    ? deriveComparisonDateRange(
        dateInterval,
        compareDateRange,
        timezone,
        timeGranularity,
      )
    : null;

  let breakdownFilters:
    | {
        breakdown: string;
        value: string;
      }[]
    | null = null;

  if (comparison.filters.length) {
    breakdownFilters = flatten(
      comparison.filters.map((breakdown) =>
        breakdown.values.map((value) => ({
          breakdown: breakdown.breakdown.key,
          value: value.value,
        })),
      ),
    );
  }

  if (commonVariables.breakdownFilters) {
    if (!breakdownFilters) {
      breakdownFilters = [];
    }

    commonVariables.breakdownFilters.forEach((e) => {
      if (e) {
        breakdownFilters?.push(e);
      }
    });

    breakdownFilters = uniqBy(
      breakdownFilters,
      (e) => `${e.breakdown} ${e.value}`,
    );
  }

  return {
    variables: {
      ...(comparisonDateRange ? { dateRange: comparisonDateRange } : {}),
      ...(breakdownFilters && { breakdownFilters }),
      ...(comparison.ignoreTableFilters && { metricFilters: null }),
    },
    useMainQuery: false,
  };
}

export const Chart = forwardRef<
  ChartImperativeRef,
  Omit<
    ChartProps,
    | "metrics"
    | "processedData"
    | "annotationDataset"
    | "processedDataCompare1"
    | "processedDataCompare2"
    | "salesReport"
    | "benchmarkMainData"
    | "benchmarkCompare1Data"
    | "benchmarkCompare2Data"
  >
>((props, ref) => {
  const { breakdowns } = usePageConfiguration();

  const { metrics } = useAvailableMetrics();

  const isSplitView = useSplitView();
  const { activeTab, isBreakdownTab } = useActiveTab();
  const { tabState } = useTabState(activeTab);

  const areRowsSelected =
    (isBreakdownTab &&
      tabState.proposedFiltersState.inclusionAtomicities.some(
        ({ atomicity }) => JSON.parse(atomicity).length >= breakdowns.length,
      )) ||
    (!isBreakdownTab && tabState.proposedFiltersState.inclusionAdKeys?.length);

  if (isSplitView && !tabState.isPreselectionFinished) {
    return (
      <div>
        <ChartConfigurator chartIndex={props.chartIndex} metrics={metrics} />
        <div className="h-[388px] mt-2 border rounded-md relative">
          <TitleSlide>
            <img
              src={salesChartLoading}
              alt="Chart loading gif"
              className="w-1/3 m-auto"
            />
          </TitleSlide>
        </div>
      </div>
    );
  }

  if (isSplitView && !areRowsSelected) {
    return (
      <div>
        <ChartConfigurator chartIndex={props.chartIndex} metrics={metrics} />
        <div className="h-[388px] mt-2 border rounded-md relative text-subtitle">
          <div
            className="absolute left-[50%] top-[50%] z-[1] text-center whitespace-nowrap"
            style={{ transform: "translate(-50%, -50%)" }}
          >
            {breakdowns.length > 0 ? (
              <>
                We can't infer table selections across tabs when chart view is
                'split' and breakdowns are active
                <br />
                You'll need to make selections in the table to view the chart
              </>
            ) : (
              <>Select rows in the table below to view chart</>
            )}
          </div>
        </div>
      </div>
    );
  }

  return <ChartContent ref={ref} {...props} />;
});

const ChartContent = forwardRef<
  ChartImperativeRef,
  Omit<
    ChartProps,
    | "metrics"
    | "processedData"
    | "annotationDataset"
    | "processedDataCompare1"
    | "processedDataCompare2"
    | "salesReport"
    | "benchmarkMainData"
    | "benchmarkCompare1Data"
    | "benchmarkCompare2Data"
  >
>((props, ref) => {
  const { user } = useUser();
  const { activeTab, isBreakdownTab } = useActiveTab();
  const { tabState } = useTabState(activeTab);
  const { addQueryMetadata } = useQueryMetadata();

  const [benchmarkMainData, setBenchmarkMainData] = useState<number | null>(
    null,
  );
  const [benchmarkCompare1Data, setBenchmarkCompare1Data] = useState<
    number | null
  >(null);
  const [benchmarkCompare2Data, setBenchmarkCompare2Data] = useState<
    number | null
  >(null);

  const isSplitMode = useSplitView();

  const { isDefaultSelectAll } = useDefaultTableSelectionIsSelectAll();

  const { metrics, nbOnlyMetrics, customMetrics, getMetricById } =
    useAvailableMetrics();

  const selectedMetrics = useMemo(() => {
    const chartMetricIds = [props.chartConfig.mainMetric.metricName];
    props.chartConfig.comparisons.forEach((comparison) => {
      if (comparison.queryKey === "metricName") {
        chartMetricIds.push(comparison.queryValue as string);
      }
    });
    return chartMetricIds
      .map((metricId) => metrics.find(({ id }) => id === metricId)!)
      .filter(Boolean);
  }, [
    props.chartConfig.comparisons,
    props.chartConfig.mainMetric.metricName,
    metrics,
  ]);

  const {
    accountingMode,
    attributionModel,
    attributionWindow,
    dateRangeInterval,
    compareDateRange,
    breakdowns,
    timeGranularity,
    granularity,
    filters,
    statuses,
    metricFilters,
    searchQuery,
  } = usePageConfiguration();

  const cleanMetricFilters = useMemo(() => {
    return isEmpty(metricFilters) ? null : metricFilters;
  }, [metricFilters]);

  const allMetrics = useMemo(() => {
    const metricsFiltersMetrics = flatten(
      cleanMetricFilters?.metricClauses?.map((m) =>
        resolveBaseMetrics(getMetricById(m.metric.id), getMetricById),
      ),
    );

    const flattenedMetrics = flatten(
      selectedMetrics.map((m) => resolveBaseMetrics(m, getMetricById)),
    );
    return orderBy(uniq([...flattenedMetrics, ...metricsFiltersMetrics]));
  }, [cleanMetricFilters?.metricClauses, selectedMetrics, getMetricById]);

  const showV1Forecasts = useShowV1Forecasts(
    timeGranularity,
    attributionModel,
    accountingMode,
  );

  const commonVariables = useMemo(() => {
    const breakdownFilters: {
      breakdown: string;
      value: string;
    }[] = filters
      .map((breakdown) =>
        breakdown.values.map((value) => ({
          breakdown: breakdown.key,
          value,
        })),
      )
      .flat();

    // reconcile the states
    const reconciledStates = reconcileAppliedAndProposedTabFilters(
      tabState.appliedFiltersState ?? null,
      tabState.proposedFiltersState,
      isDefaultSelectAll,
    );

    const { toAddExclusions, appliedAdKeyFilters } =
      enrichInclusionsAndExclusions({
        breakdowns,
        reconciledStates,
        currentTabState: tabState,
      });

    const exclusionBreakdownFilters = [
      ...reconciledStates.exclusionAtoms,
      ...toAddExclusions,
    ].map(({ atomicity }) => {
      const parsed = JSON.parse(atomicity) as Dimensions[];
      return flatten(
        parsed.map((dimensions) => {
          return Object.keys(dimensions).map((key) => ({
            breakdown: key,
            value: dimensions[key],
          }));
        }),
      );
    });

    const inclusionBreakdownFilters = reconciledStates.inclusionAtoms.map(
      ({ atomicity }) => {
        const parsed = JSON.parse(atomicity) as Dimensions[];
        return flatten(
          parsed.map((dimensions) => {
            return Object.keys(dimensions).map((key) => ({
              breakdown: key,
              value: dimensions[key],
            }));
          }),
        );
      },
    );

    const exclusionTabFilter =
      activeTab === "breakdown"
        ? "exclusionCampaignFilters"
        : `exclusion${sentenceCase(activeTab)}Filters`;
    const inclusionTabFilter =
      activeTab === "breakdown" ? "campaignFilters" : `${activeTab}Filters`;

    const atomicity = isBreakdownTab ? breakdowns.map((i) => i.key) : null;
    if (isSplitMode && isBreakdownTab && atomicity?.length === 0) {
      atomicity.push("Platform (Northbeam)");
    }

    const metricFiltersCopy = cloneDeep(cleanMetricFilters);
    metricFiltersCopy?.metricClauses?.forEach((metricClause) => {
      if (metricClause.metric.details) {
        metricClause.metric.details.am = attributionModel;
      }
    });

    return {
      queryType: isSplitMode
        ? "specific"
        : !isDefaultSelectAll
        ? "exclusive"
        : "inclusive",
      includeLabelsSearch: isBreakdownTab,
      showV1Forecasts,
      metrics: allMetrics,
      dateRange: dateRangeInterval,
      attributionMethod: attributionModel,
      attributionWindow,
      accountingMode,
      timeGranularity,
      offset: 0,
      limit: 100,
      statuses,
      searchText: null,
      creativeType: null,
      metricFilters: metricFiltersCopy,
      inclusionBreakdownFilters,
      exclusionBreakdownFilters,
      atomicity,
      breakdownFilters,
      dateCompression: false,
      aggregateAll: props.chartConfig.chartView === "aggregate",
      search:
        searchQuery && searchQuery.query !== ""
          ? {
              method: searchQuery.method,
              operator: searchQuery.operator,
              query: searchQuery.query,
            }
          : null,
      sorting: null,

      exclusionCampaignFilters:
        tabState?.appliedFiltersState?.campaignExclusionAdKeys?.map(
          (i) => i.adKey,
        ),
      exclusionAdsetFilters:
        tabState?.appliedFiltersState?.adsetExclusionAdKeys?.map(
          (i) => i.adKey,
        ),
      campaignFilters: appliedAdKeyFilters?.campaignInclusionFilters,
      adsetFilters: appliedAdKeyFilters?.adsetInclusionFilters,
      adFilters: tabState?.appliedFiltersState?.adsetInclusionAdKeys,

      [inclusionTabFilter]: tabState.proposedFiltersState.inclusionAdKeys,
      [exclusionTabFilter]: tabState.proposedFiltersState.exclusionAdKeys?.map(
        (i) => i.adKey,
      ),
    } as Omit<GetSalesReportV3AugmentedVariables, "granularity">;
  }, [
    props.chartConfig.chartView,
    tabState,
    isDefaultSelectAll,
    breakdowns,
    activeTab,
    isBreakdownTab,
    isSplitMode,
    showV1Forecasts,
    dateRangeInterval,
    attributionModel,
    attributionWindow,
    accountingMode,
    timeGranularity,
    statuses,
    cleanMetricFilters,
    searchQuery,
    filters,
    allMetrics,
  ]);
  const mainVariables = useMemo(() => {
    const mainMetric = props.chartConfig.mainMetric;
    let breakdownFilters:
      | {
          breakdown: string;
          value: string;
        }[]
      | null = null;

    if (mainMetric.filters.length) {
      breakdownFilters = mainMetric.filters
        .map((breakdown) =>
          breakdown.values.map((value) => ({
            breakdown: breakdown.breakdown.key,
            value: value.value,
          })),
        )
        .flat();
    }

    if (commonVariables.breakdownFilters) {
      if (!breakdownFilters) {
        breakdownFilters = [];
      }

      commonVariables.breakdownFilters.forEach((e) => {
        if (e) {
          breakdownFilters?.push(e);
        }
      });

      breakdownFilters = uniqBy(
        breakdownFilters,
        (e) => `${e.breakdown} ${e.value}`,
      );
    }

    return {
      ...commonVariables,
      ...(breakdownFilters && { breakdownFilters }),
      ...(mainMetric.ignoreTableFilters && { metricFilters: null }),
    } as Omit<GetSalesReportV3AugmentedVariables, "granularity">;
  }, [props.chartConfig.mainMetric, commonVariables]);

  const { loadAllData, data, isLoading } = useSalesReportV3(
    granularity,
    mainVariables,
  );

  const {
    loadAllData: loadCompareTo1AllData,
    data: compareTo1Data,
    isLoading: compareTo1Loading,
  } = useSalesReportV3(granularity, commonVariables);

  const {
    loadAllData: loadCompareTo2AllData,
    data: compareTo2Data,
    isLoading: compareTo2Loading,
  } = useSalesReportV3(granularity, commonVariables);

  const loadAdoptedBenchmarks = useUniversalBenchmarksLoader();

  useEffect(() => {
    loadAllData({
      showV1Forecasts:
        mainVariables.showV1Forecasts &&
        props.chartConfig.mainMetric.metricName
          .toLowerCase()
          .includes("forecast"),
    });
  }, [
    loadAllData,
    mainVariables.showV1Forecasts,
    props.chartConfig.mainMetric.metricName,
  ]);

  useEffect(() => {
    const metric = getMetricById(props.chartConfig.mainMetric.metricName);

    loadAdoptedBenchmarks({
      attributionModel: mainVariables.attributionMethod,
      accountingMode: mainVariables.accountingMode,
      attributionWindow: mainVariables.attributionWindow,
      level: granularity === "platform" ? "campaign" : granularity,
      breakdowns: [],
      filters: mainVariables.breakdownFilters,
      metric: metric
        ? { id: metric.metadata.baseId, isLtv: metric.metadata.isLtv }
        : undefined,
    }).then((value) => {
      setBenchmarkMainData(value?.["topLevel"] ?? null);
    });
  }, [
    getMetricById,
    loadAdoptedBenchmarks,
    props.chartConfig.mainMetric.metricName,
    mainVariables.attributionMethod,
    mainVariables.accountingMode,
    mainVariables.attributionWindow,
    mainVariables.breakdownFilters,
    granularity,
  ]);

  useEffect(() => {
    const comparison1 = props.chartConfig.comparisons?.[0];
    const comparison2 = props.chartConfig.comparisons?.[1];

    if (comparison1) {
      const comparison1Variables =
        comparison1.queryKey === "compareDateRange"
          ? createCompareDateRangeComparisonVariables(
              comparison1,
              dateRangeInterval,
              compareDateRange,
              user.timezone,
              timeGranularity,
              commonVariables,
            )
          : createComparisonVariables(
              comparison1,
              props.chartConfig.mainMetric,
              commonVariables,
            );

      if (!comparison1Variables.useMainQuery) {
        loadCompareTo1AllData(comparison1Variables.variables);
      }
    }
    if (comparison2) {
      const comparison2Variables =
        comparison2.queryKey === "compareDateRange"
          ? createCompareDateRangeComparisonVariables(
              comparison2,
              dateRangeInterval,
              compareDateRange,
              user.timezone,
              timeGranularity,
              commonVariables,
            )
          : createComparisonVariables(
              comparison2,
              props.chartConfig.mainMetric,
              commonVariables,
            );
      !comparison2Variables.useMainQuery &&
        loadCompareTo2AllData(comparison2Variables.variables);
    }
  }, [
    user.timezone,
    loadCompareTo1AllData,
    loadCompareTo2AllData,
    props.chartConfig.comparisons,
    compareDateRange,
    commonVariables,
    timeGranularity,
    dateRangeInterval,
    props.chartConfig.mainMetric,
  ]);

  useEffect(() => {
    const comparison1 = props.chartConfig.comparisons?.[0];
    const comparison2 = props.chartConfig.comparisons?.[1];

    if (comparison1) {
      const metric = getMetricById(
        comparison1.queryKey === "metricName"
          ? comparison1.queryValue
          : props.chartConfig.mainMetric.metricName,
      );

      const comparison1Variables =
        comparison1.queryKey === "compareDateRange"
          ? createCompareDateRangeComparisonVariables(
              comparison1,
              dateRangeInterval,
              compareDateRange,
              user.timezone,
              timeGranularity,
              commonVariables,
            )
          : createComparisonVariables(
              comparison1,
              props.chartConfig.mainMetric,
              commonVariables,
            );

      const variables = {
        ...commonVariables,
        ...comparison1Variables.variables,
      };

      loadAdoptedBenchmarks({
        attributionModel: variables.attributionMethod,
        accountingMode: variables.accountingMode,
        attributionWindow: variables.attributionWindow,
        level: granularity === "platform" ? "campaign" : granularity,
        breakdowns: [],
        filters: variables.breakdownFilters,
        metric: metric
          ? { id: metric.metadata.baseId, isLtv: metric.metadata.isLtv }
          : undefined,
      }).then((value) => {
        setBenchmarkCompare1Data(value?.["topLevel"] ?? null);
      });
    }

    if (comparison2) {
      const metric = getMetricById(
        comparison2.queryKey === "metricName"
          ? comparison2.queryValue
          : props.chartConfig.mainMetric.metricName,
      );

      const comparison2Variables =
        comparison2.queryKey === "compareDateRange"
          ? createCompareDateRangeComparisonVariables(
              comparison2,
              dateRangeInterval,
              compareDateRange,
              user.timezone,
              timeGranularity,
              commonVariables,
            )
          : createComparisonVariables(
              comparison2,
              props.chartConfig.mainMetric,
              commonVariables,
            );

      const variables = {
        ...commonVariables,
        ...comparison2Variables.variables,
      };

      loadAdoptedBenchmarks({
        attributionModel: variables.attributionMethod,
        accountingMode: variables.accountingMode,
        attributionWindow: variables.attributionWindow,
        level: granularity === "platform" ? "campaign" : granularity,
        breakdowns: [],
        filters: variables.breakdownFilters,
        metric: metric
          ? { id: metric.metadata.baseId, isLtv: metric.metadata.isLtv }
          : undefined,
      }).then((value) => {
        setBenchmarkCompare2Data(value?.["topLevel"] ?? null);
      });
    }
  }, [
    getMetricById,
    loadAdoptedBenchmarks,
    props.chartConfig.mainMetric,
    props.chartConfig.comparisons,
    user.timezone,
    compareDateRange,
    commonVariables,
    timeGranularity,
    dateRangeInterval,
  ]);

  const salesReport = data?.me?.salesTableReportV3Augmented?.actual;

  const annotationDataset = useBuildAnnotationDataset(
    dateRangeInterval.startDate,
    dateRangeInterval.endDate,
    user.timezone,
  );

  const processData = useCallback(
    (
      report:
        | GetSalesReportV3Augmented_me_salesTableReportV3Augmented["actual"]
        | undefined,
    ) => {
      const processedData: ChartDatasets = report?.rows
        ? preprocessForChart(
            nbOnlyMetrics,
            customMetrics,
            report.rows,
            user.timezone,
          )
        : { datasets: [] };

      return processedData;
    },
    [user.timezone, nbOnlyMetrics, customMetrics],
  );

  const processedData = useMemo(() => {
    return processData(salesReport);
  }, [processData, salesReport]);

  const processedDataCompare1 = useMemo(() => {
    const comparison1 = props.chartConfig.comparisons?.[0];
    if (!comparison1) {
      return null;
    }

    const { useMainQuery } =
      comparison1.queryKey === "compareDateRange"
        ? createCompareDateRangeComparisonVariables(
            comparison1,
            dateRangeInterval,
            compareDateRange,
            user.timezone,
            timeGranularity,
            commonVariables,
          )
        : createComparisonVariables(
            comparison1,
            props.chartConfig.mainMetric,
            commonVariables,
          );

    if (useMainQuery) {
      return processData(salesReport);
    }

    return processData(compareTo1Data?.me?.salesTableReportV3Augmented?.actual);
  }, [
    compareDateRange,
    compareTo1Data?.me?.salesTableReportV3Augmented?.actual,
    dateRangeInterval,
    commonVariables,
    processData,
    props.chartConfig.comparisons,
    props.chartConfig.mainMetric,
    salesReport,
    timeGranularity,
    user.timezone,
  ]);

  const processedDataCompare2 = useMemo(() => {
    const comparison2 = props.chartConfig.comparisons?.[1];
    if (!comparison2) {
      return null;
    }

    const { useMainQuery } =
      comparison2.queryKey === "compareDateRange"
        ? createCompareDateRangeComparisonVariables(
            comparison2,
            dateRangeInterval,
            compareDateRange,
            user.timezone,
            timeGranularity,
            commonVariables,
          )
        : createComparisonVariables(
            comparison2,
            props.chartConfig.mainMetric,
            commonVariables,
          );

    if (useMainQuery) {
      return processData(salesReport);
    }

    return processData(compareTo2Data?.me?.salesTableReportV3Augmented?.actual);
  }, [
    compareDateRange,
    compareTo2Data?.me?.salesTableReportV3Augmented?.actual,
    dateRangeInterval,
    commonVariables,
    processData,
    props.chartConfig.comparisons,
    props.chartConfig.mainMetric,
    salesReport,
    timeGranularity,
    user.timezone,
  ]);

  useEffect(() => {
    if (data?.me?.salesTableReportV3Augmented?.queryMetadata?.bqLogs) {
      addQueryMetadata({
        context: "Sales v3",
        topic: "Chart Selects Main",
        metadata: data.me.salesTableReportV3Augmented.queryMetadata,
      });
    }
  }, [data?.me?.salesTableReportV3Augmented?.queryMetadata]);

  useEffect(() => {
    if (
      compareTo1Data?.me?.salesTableReportV3Augmented?.queryMetadata?.bqLogs
    ) {
      addQueryMetadata({
        context: "Sales v3",
        topic: "Chart Selects Compare 1",
        metadata: compareTo1Data.me.salesTableReportV3Augmented.queryMetadata,
      });
    }
  }, [compareTo1Data?.me?.salesTableReportV3Augmented?.queryMetadata]);

  useEffect(() => {
    if (compareTo2Data?.me?.salesTableReportV3Augmented?.queryMetadata) {
      addQueryMetadata({
        context: "Sales v3",
        topic: "Chart Selects Compare 2",
        metadata: compareTo2Data.me.salesTableReportV3Augmented.queryMetadata,
      });
    }
  }, [compareTo2Data?.me?.salesTableReportV3Augmented?.queryMetadata]);

  const orderedMetrics = useMemo(() => orderBy(metrics, "name"), [metrics]);

  if (isLoading || compareTo1Loading || compareTo2Loading) {
    return (
      <div>
        <ChartConfigurator chartIndex={props.chartIndex} metrics={metrics} />
        <div className="h-[388px] mt-2 border rounded-md relative">
          <TitleSlide>
            <img
              src={salesChartLoading}
              alt="Chart loading gif"
              className="w-1/3 m-auto"
            />
          </TitleSlide>
        </div>
      </div>
    );
  }

  return (
    <>
      <ChartInner
        ref={ref}
        {...props}
        metrics={orderedMetrics}
        processedData={processedData}
        annotationDataset={annotationDataset}
        processedDataCompare1={processedDataCompare1}
        processedDataCompare2={processedDataCompare2}
        salesReport={salesReport}
        benchmarkMainData={benchmarkMainData}
        benchmarkCompare1Data={benchmarkCompare1Data}
        benchmarkCompare2Data={benchmarkCompare2Data}
      />
    </>
  );
});

const ChartInner = forwardRef<ChartImperativeRef, ChartProps>(
  (
    {
      metrics,
      chartConfig,
      chartIndex,
      processedData,
      annotationDataset,
      processedDataCompare1,
      processedDataCompare2,
      salesReport,
      benchmarkMainData,
      benchmarkCompare1Data,
      benchmarkCompare2Data,
    },
    ref,
  ) => {
    const { user } = useUser();
    const { activeTab } = useActiveTab();
    const plugins = usePlugins(user.currencies.currentCurrency);
    const {
      accountingMode,
      granularity,
      flyoutTab,
      attributionModel,
      compareDateRange,
      attributionWindow,
      dateRangeInterval,
      breakdowns,
      filters,
      timeGranularity,
    } = usePageConfiguration();

    const getCompareQueryMetadata = useMemo(() => {
      return buildCompareQueryMetadata({
        metrics,
        attributionMethodEnum: user.attributionMethodEnum,
        compareDateRange,
      });
    }, [metrics, user, compareDateRange]);

    const [chartRef, setChartRef] = useState<ChartJSOrUndefined>();
    const setChartInstance = useCallback(
      (instance: ChartJSOrUndefined | null) => {
        if (instance) {
          setChartRef(instance);
        }
      },
      [],
    );

    useEffect(() => {
      chartRef && debouncedResize(chartRef);
    }, [
      chartRef,
      accountingMode,
      granularity,
      flyoutTab,
      attributionModel,
      compareDateRange,
      attributionWindow,
      dateRangeInterval,
      breakdowns,
      timeGranularity,
      annotationDataset,
    ]);

    const selectedMetrics = useMemo(() => {
      const chartMetricIds = [chartConfig.mainMetric.metricName];
      chartConfig.comparisons.forEach((comparison) => {
        if (comparison.queryKey === "metricName") {
          chartMetricIds.push(comparison.queryValue as string);
        }
      });
      return chartMetricIds
        .map((metricId) => metrics.find(({ id }) => id === metricId)!)
        .filter(Boolean);
    }, [chartConfig.comparisons, chartConfig.mainMetric.metricName, metrics]);

    const selectedCompareMetrics = useMemo(() => {
      const mainMetric = selectedMetrics[0];

      return compact(
        chartConfig.comparisons.map((comparison) => {
          if (
            comparison.queryKey === "metricName" ||
            comparison.queryKey === "compareDateRange" ||
            !mainMetric
          ) {
            return null;
          }

          const adjustedAttributionModel =
            comparison.queryKey === "attributionModel"
              ? comparison.queryValue
              : attributionModel;

          const adjustedAttributionWindow =
            comparison.queryKey === "attributionWindow"
              ? comparison.queryValue
              : attributionWindow;

          const adjustedAccountingMode =
            comparison.queryKey === "accountingMode"
              ? comparison.queryValue
              : accountingMode;

          const metrics = uniqBy(
            [
              ...makeOverviewRows(
                adjustedAttributionModel,
                adjustedAttributionWindow,
                salesReport?.hasV1Forecasts ?? false,
                adjustedAccountingMode,
                salesReport?.customGoals ?? [],
                timeGranularity,
                user as unknown as AbstractUser,
              ),
              ...makeTrafficRows(
                adjustedAttributionModel,
                adjustedAttributionWindow,
                adjustedAccountingMode,
                user as unknown as AbstractUser,
                timeGranularity,
              ),
              ...makeFirstTimeVsReturningRows(
                adjustedAttributionModel,
                adjustedAttributionWindow,
                adjustedAccountingMode,
                timeGranularity,
                user.attributionMethodEnum,
              ),
              ...makeBenchmarkRows(
                adjustedAttributionModel,
                adjustedAttributionWindow,
              ),
            ],
            "id",
          );

          return metrics.find(
            (m) => m.metadata?.baseId === mainMetric.metadata?.baseId,
          );
        }),
      );
    }, [
      selectedMetrics,
      chartConfig,
      accountingMode,
      attributionModel,
      attributionWindow,
      salesReport?.customGoals,
      salesReport?.hasV1Forecasts,
      timeGranularity,
      annotationDataset,
      user,
    ]);

    useImperativeHandle(
      ref,
      () => ({
        exportData: () => {
          downloadCsv(
            `north-beam-chart-data-${chartIndex + 1}.csv`,
            chartConfig.comparisons,
            [...selectedMetrics, ...selectedCompareMetrics],
            [
              processedData.datasets,
              processedDataCompare1?.datasets,
              processedDataCompare2?.datasets,
            ],
            timeGranularity,
            user,
          );
        },
      }),
      [
        chartIndex,
        chartConfig,
        selectedMetrics,
        selectedCompareMetrics,
        processedData,
        processedDataCompare1,
        processedDataCompare2,
        timeGranularity,
        user,
      ],
    );

    const { designElementManager } = useDesignElementManager();

    useEffect(() => {
      const compareDateRangeInterval = compareDateRange
        ? deriveComparisonDateRange(
            dateRangeInterval,
            compareDateRange,
            user.timezone,
            timeGranularity,
          )
        : null;

      let compareDateRangeLabels: Date[] | undefined;
      if (compareDateRangeInterval) {
        const dateRangeDiff = differenceInDays(
          new Date(dateRangeInterval.endDate),
          new Date(dateRangeInterval.startDate),
        );
        const endDate = addDays(
          getDateOnly(compareDateRangeInterval.startDate, user.timezone),
          dateRangeDiff + 1,
        );
        const formatString = "YYYY-MM-DD";
        const finalEndDate = moment
          .tz(endDate, user.timezone)
          .format(formatString);
        compareDateRangeLabels = generateDateLabels(
          {
            startDate: compareDateRangeInterval.startDate,
            endDate: finalEndDate,
          },
          user.timezone,
          timeGranularity,
        );
      }

      setChartDataManagerOptions(chartRef, {
        data: processedData,
        labels: generateDateLabels(
          dateRangeInterval,
          user.timezone,
          timeGranularity,
        ),
        compareDateRangeLabels,
        comparisonDatasets: [processedDataCompare1, processedDataCompare2],
        selectedCompareMetrics,
        selectedMetrics,
        benchmarkMainData,
        benchmarkCompare1Data,
        benchmarkCompare2Data,
        getCompareQueryMetadata,
        activeTab,
        pageConfiguration: {
          dateRange: dateRangeInterval,
          compareDateRange:
            compareDateRangeInterval as constants.CompareDateRange<"custom">,
          dateRangeInterval,
          compareDateRangeInterval,
          attributionModel: attributionModel,
          attributionWindow,
          accountingMode,
          timeGranularity,
          sorting: null,
          metricFilters: {
            clauseType: CreativesFilterClauseTypeEnum.AND,
            metricClauses: [],
          },
          breakdowns,
          filters,
          granularity,
          flyoutTab,

          // TODO: Type this better
          offset: 0,
          limit: 8,
          status: null,
          searchText: null,
          creativeType: null,
          atomicity: breakdowns.map((i) => i.key),
          // TODO: use breakdowns to create the breakdown filters
          breakdownFilters: null,
          dateCompression: false,
          timezone: user.timezone,
        } as any,
        chartConfig,
        annotationDataset,
        designElementManager,
      });

      setYAxesTitles({
        chart: chartRef,
        selectedMetrics,
        code: user.currencies.currentCurrency,
      });
      updateScaleMax(chartRef, chartConfig.alignAxesScale);
    }, [
      chartRef,
      selectedMetrics,
      processedData,
      flyoutTab,
      granularity,
      user.timezone,
      user.currencies.currentCurrency,
      getCompareQueryMetadata,
      chartConfig,
      activeTab,
      compareDateRange,
      annotationDataset,
      dateRangeInterval,
      attributionModel,
      attributionWindow,
      accountingMode,
      timeGranularity,
      breakdowns,
      filters,
      processedDataCompare1,
      processedDataCompare2,
      designElementManager,
      selectedCompareMetrics,
      benchmarkMainData,
      benchmarkCompare1Data,
      benchmarkCompare2Data,
    ]);

    const options: ChartOptions<any> = useMemo(() => {
      return getChartOptions({
        processedData,
        processedDataCompare1,
        processedDataCompare2,
        benchmarkMainData,
        benchmarkCompare1Data,
        benchmarkCompare2Data,
        selectedMetrics,
        selectedCompareMetrics,
        timeGranularity,
        code: user.currencies.currentCurrency,
        animation: true,
      });
    }, [
      processedData,
      processedDataCompare1,
      processedDataCompare2,
      benchmarkMainData,
      benchmarkCompare1Data,
      benchmarkCompare2Data,
      selectedMetrics,
      selectedCompareMetrics,
      timeGranularity,
      user.currencies.currentCurrency,
    ]);

    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.2 }}
        data-testid="sales-v3-chart"
      >
        <ChartConfigurator chartIndex={chartIndex} metrics={metrics} />
        <ChartLegend chartIndex={chartIndex} chart={chartRef} />
        <div className="h-96">
          <Line
            ref={setChartInstance}
            plugins={plugins}
            options={options}
            data={{ datasets }}
          />
        </div>
      </motion.div>
    );
  },
);
